import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useUIStore } from '@/store'
import {
  LayoutDashboard,
  TrendingUp,
  Briefcase,
  History,
  Settings,
  HelpCircle,
  Github,
  CreditCard,
  Trophy,
} from 'lucide-react'
import { cn } from '@/utils/cn'

export const Sidebar: React.FC = () => {
  const { isSidebarOpen, toggleSidebar } = useUIStore()
  const navigate = useNavigate()

  const navItems = [
    { id: 'dashboard', label: 'Главная', icon: LayoutDashboard, path: '/dashboard' },
    { id: 'invest', label: 'Инвестиции', icon: TrendingUp, path: '/invest' },
    { id: 'career', label: 'Карьера', icon: Briefcase, path: '/career' },
    { id: 'history', label: 'История', icon: History, path: '/history' },
    { id: 'expenses', label: 'Расходы', icon: CreditCard, path: '/expenses' },
    { id: 'achievements', label: 'Достижения', icon: Trophy, path: '/achievements'}
  ]

  const bottomItems = [
    { id: 'settings', label: 'Настройки', icon: Settings, path: '/settings' },
    { id: 'help', label: 'Помощь', icon: HelpCircle, path: '/help' },
    {
      id: 'github',
      label: 'GitHub',
      icon: Github,
      href: 'https://github.com/ledneev/handleFinance',
    },
  ]

  // Хелпер: закрывает сайдбар на мобильных
  const handleNavigate = (path: string) => {
    navigate(path)
    if (window.innerWidth < 1024) {
      toggleSidebar()
    }
  }

  return (
    <>

      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden" onClick={toggleSidebar} />
      )}

      <aside
        className={cn(
          'fixed top-16 left-0 h-[calc(100vh-4rem)] w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-40 transition-transform duration-300 ease-in-out',
          'lg:translate-x-0',
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          <nav className="flex-1 p-4">
            <div className="mb-8">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                Основное
              </h3>
              <ul className="space-y-1">
                {navItems.map(item => {
                  const Icon = item.icon
                  const isActive = window.location.pathname === item.path

                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => handleNavigate(item.path)}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
                          isActive
                            ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
                        )}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        <Icon className="h-5 w-5 flex-shrink-0" />
                        <span className="font-medium">{item.label}</span>
                        {isActive && <div className="ml-auto w-2 h-2 bg-blue-500 rounded-full" />}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          </nav>

          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <ul className="space-y-1">
              {bottomItems.map(item => {
                const Icon = item.icon
                const isActive = item.path ? window.location.pathname === item.path : false

                // GitHub — внешняя ссылка
                if (item.id === 'github') {
                  return (
                    <li key={item.id}>
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
                          'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
                        )}
                        aria-label="Открыть репозиторий на GitHub"
                      >
                        <Icon className="h-5 w-5 flex-shrink-0" />
                        <span className="font-medium">{item.label}</span>
                        <svg
                          className="h-4 w-4 opacity-70 text-gray-500 dark:text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    </li>
                  )
                }

                return (
                  <li key={item.id}>
                    <button
                      onClick={() => handleNavigate(item.path!)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
                        isActive
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
                      )}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <Icon className="h-5 w-5 flex-shrink-0" />
                      <span className="font-medium">{item.label}</span>
                      {isActive && <div className="ml-auto w-2 h-2 bg-blue-500 rounded-full" />}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      </aside>
    </>
  )
}
